import { ComboboxOption, ComboboxOptions } from '@headlessui/react';
import Icon from '@kk/ui/components/Icon';
import MarkMatches from '@kk/ui/components/MarkMatches';
import Pill from '@kk/ui/components/Pill';
import { cn } from 'cn';
import isPast from 'date-fns/isPast';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import type api from '#/api';
import {
  MIN_SEARCH_QUERY_LENGTH,
  type Response as SearchResponse,
} from '#/api/hooks/useSearchResults';
import type { SavedSearch } from './useSavedSearch';

type SearchResult = api.LoanDocument | SavedSearch;

interface SearchResultsProps {
  open: boolean;
  query: string;
  data: SearchResponse | undefined;
  savedSearches: { loans: SavedSearch[] };
  onOptionInteraction: () => void;
  showOptionFocus: boolean;
}

type SearchResultsState =
  | { type: 'recent' }
  | { type: 'empty' }
  | { type: 'results'; count: number }
  | { type: 'hidden' };

const supportedLoanTypes = new Set([
  'kkfast',
  'kkvar',
  'kkcibor',
  'byggekredit',
]);

export function SearchResults({
  open,
  query,
  data,
  savedSearches,
  onOptionInteraction,
  showOptionFocus,
}: SearchResultsProps): React.ReactNode {
  const { t } = useTranslation();
  const showSavedSearchResults =
    query.length < MIN_SEARCH_QUERY_LENGTH && savedSearches.loans.length > 0;
  const loans = showSavedSearchResults ? savedSearches.loans : data?.loans;
  const state = getSearchResultsState(
    showSavedSearchResults,
    query,
    loans?.length ?? 0,
  );
  const title = match(state)
    .with({ type: 'recent' }, () => t('search.latest'))
    .with({ type: 'empty' }, () => t('search.noResults'))
    .with({ type: 'results' }, ({ count }) => `${t('loan_other')} (${count})`)
    .with({ type: 'hidden' }, () => null)
    .exhaustive();

  if (!open || title === null) return null;

  return (
    <ComboboxOptions
      as="ol"
      static
      // Keep the adjacent clear and close controls interactive while results are open.
      modal={false}
      className="text-neutral-110 absolute z-10 mt-2 max-h-80 w-full overflow-auto rounded-md bg-white shadow-lg border border-neutral-10 focus:outline-hidden"
    >
      <div className="pt-2">
        <div className="py-4 px-6 typ-headline-xs text-neutral-100">
          {title}
        </div>
        {loans?.map((loan) => (
          <SearchResultOption
            key={loan.contractNumber}
            loan={loan}
            query={query}
            onOptionInteraction={onOptionInteraction}
            showOptionFocus={showOptionFocus}
          />
        ))}
      </div>
    </ComboboxOptions>
  );
}

function getSearchResultsState(
  showSavedSearchResults: boolean,
  query: string,
  loanCount: number,
): SearchResultsState {
  if (showSavedSearchResults) return { type: 'recent' };
  if (query.length < MIN_SEARCH_QUERY_LENGTH) return { type: 'hidden' };
  if (loanCount === 0) return { type: 'empty' };
  return { type: 'results', count: loanCount };
}

function SearchResultOption({
  loan,
  query,
  onOptionInteraction,
  showOptionFocus,
}: {
  loan: SearchResult;
  query: string;
  onOptionInteraction: () => void;
  showOptionFocus: boolean;
}): React.ReactNode {
  const { t } = useTranslation();
  const isSupported = supportedLoanTypes.has(
    loan.loanType?.toLowerCase() ?? 'no match',
  );
  const isExpired = loan.maturityDate
    ? isPast(new Date(loan.maturityDate))
    : false;

  return (
    <ComboboxOption
      as="li"
      className={({ focus }) =>
        cn(
          'p-3 group relative flex justify-between cursor-pointer list-none items-center space-x-4',
          'aria-disabled:text-neutral-60 aria-disabled:pointer-events-none',
          {
            'bg-blue-5 text-blue-60': focus && showOptionFocus,
            'text-neutral-110': !focus || !showOptionFocus,
          },
        )
      }
      value={loan}
      disabled={!isSupported}
      onPointerMove={onOptionInteraction}
    >
      <div>
        <div className="flex space-x-4 items-center">
          <Icon
            type="loan-alt"
            className="text-blue-60 group-aria-disabled:text-neutral-40 shrink-0"
            size="small"
            solid
          />
          <div className="typ-body">
            <div>
              <MarkMatches
                text={`${t('loan_one')} ${loan.contractNumber}`}
                search={query}
                matchClassName="font-bold bg-inherit text-inherit"
              />
            </div>
            {isExpired || !isSupported ? (
              <div
                className={cn('text-2xs leading-1', {
                  'text-neutral-100': isSupported,
                  'text-neutral-40': !isSupported,
                })}
              >
                {!isSupported ? t('unavailable') : t('search.expiredLoan')}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {loan.loanType ? (
        <Pill
          text={loan.loanType}
          className="group-aria-disabled:text-neutral-60"
        />
      ) : null}
    </ComboboxOption>
  );
}
